import React, { FC, useCallback, useMemo } from 'react'

import { Skeleton, Typography, useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import { intervalToDuration } from 'date-fns'
import { isEqual, isNaN } from 'lodash'
import { useTranslation } from 'react-i18next'

import { CustomCheckBox } from '../../../components/CustomCheckBox'
import { InfoButton } from '../../../components/InfoButton'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { toggleSmallWidget } from '../DashboardSlice'
import { MoneyValue } from '../MoneyValue'
import { SmallWidgetPropsType, smallWidgetTypes } from '../types'

const SmallWidgetComponent: FC<SmallWidgetPropsType> = ({
  name,
  mainInfo,
  currency,
  label,
  type,
  explanation,
  children,
  isLoading,
  sx,
}) => {
  const { t } = useTranslation()
  const { spacing, palette } = useTheme()
  const dispatch = useAppDispatch()

  const { customize, smallWidgetsList } = useAppSelector(({ dashboard }) => dashboard)

  const handleCheck = useCallback(() => {
    if (name) dispatch(toggleSmallWidget(name))
  }, [name])

  const info = useMemo(() => {
    if (type === smallWidgetTypes.money) {
      return (
        <MoneyValue
          isLoading={isLoading}
          money={mainInfo as number}
          currency={currency as string}
          bigFont
        />
      )
    } else if (type === smallWidgetTypes.percents) {
      return (
        <Box>
          <Typography component="span" variant="h3">
            {isLoading ? (
              <Skeleton />
            ) : mainInfo && !isNaN(mainInfo) ? (
              Number(mainInfo) < 0.01 && Number(mainInfo) > 0 ? (
                '<0.01%'
              ) : (
                `${Number(mainInfo).toFixed(2)}%`
              )
            ) : (
              '-'
            )}
          </Typography>
        </Box>
      )
    } else if (type === smallWidgetTypes.time) {
      const duration = intervalToDuration({ start: 0, end: (mainInfo as number) || 0 })

      const days = duration.days
      const hours = duration.hours
      const minutes = duration.minutes
      return (
        <Box display="flex" gap={spacing(0.5)}>
          <Box>
            <Typography component="span" variant="h3">
              {isLoading ? <Skeleton width={35} /> : days ?? '-'}
            </Typography>
            {isLoading ? null : (
              <Typography component="span" variant="h6">
                {t('timeUnitDaysShort', 'd')}
              </Typography>
            )}
          </Box>

          <Box>
            <Typography component="span" variant="h3">
              {isLoading ? <Skeleton width={30} /> : hours ?? '-'}
            </Typography>
            {isLoading ? null : (
              <Typography component="span" variant="h6">
                {t('timeUnitHoursShort', 'h')}
              </Typography>
            )}
          </Box>

          <Box>
            <Typography component="span" variant="h3">
              {isLoading ? <Skeleton width={30} /> : minutes ?? '-'}
            </Typography>
            {isLoading ? null : (
              <Typography component="span" variant="h6">
                {t('timeUnitMinutesShort', 'm')}
              </Typography>
            )}
          </Box>
        </Box>
      )
    } else if (type === smallWidgetTypes.number) {
      return (
        <Box>
          <Typography component="span" variant="h3">
            {isLoading ? <Skeleton /> : mainInfo ? Number(mainInfo).toFixed(2) : '-'}
          </Typography>
        </Box>
      )
    }
  }, [mainInfo, isLoading, type, currency])

  return (
    <Box
      minWidth={spacing(25.5)}
      minHeight={spacing(12)}
      borderRadius={spacing(0.5)}
      padding={spacing(2)}
      bgcolor={palette.background.paper}
      sx={sx}
      display="flex"
      flex={1}
      position="relative"
    >
      {customize && name && (
        <CustomCheckBox
          checked={smallWidgetsList[name]}
          sx={{ position: 'absolute', top: spacing(0.5), right: spacing(0.5) }}
          onChange={handleCheck}
        />
      )}
      <Box display="flex" flexDirection="column" justifyContent={'center'}>
        <Box display="flex" gap={spacing(0.5)}>
          <Typography fontSize={13} fontWeight="bold" color={palette.grey[700]} whiteSpace="nowrap">
            {label}
          </Typography>
          <InfoButton explanation={explanation} />
        </Box>
        {info}
      </Box>
      {children && (
        <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
          {children}
        </Box>
      )}
    </Box>
  )
}

export const SmallWidget = React.memo(SmallWidgetComponent, isEqual)
